import { Button, Pending } from '@cluey/cluey-components';
import { isArray } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { cancelEnrolment } from '../../../../actions/hub/hubCancelActions';
import { api } from '../../../../api';
import { Enrolment } from '../../../../api/types/enrolment';
import Cross from '../../../../assets/images/icon-filled-cross-dark.svg';
import CheckTickIcon from '../../../../assets/images/icon-filled-success-tick.svg';
import CalendarIcon from '../../../../assets/images/icon-outline-calendar.svg';
import { InlineLoader } from '../../../../common/InlineLoader';
import { DatePicker, useDatePickerProps } from '../../../../components/DatePicker/DatePicker';
import { SkeletonLoader } from '../../../../components/SkeletonLoader/SkeletonLoader';
import { PAYMENT_STATUS } from '../../../../types/hubEnums';
import { formatError } from '../../../../util/errors';
import { SUBSCRIPTION_LOCAL_STORAGE_KEYS, SubscriptionLocalStorage } from '../SubscriptionLocalStorage';
import useSubscriptionContext, { CANCELLATION_TYPE, SUBSCRIPTION_TYPE } from '../useSubscriptionContext';

interface CancellationProps {
  onClose?: () => void;
  enrolment?: Enrolment;
}

export const Cancellation = ({ onClose, enrolment }: CancellationProps) => {
  const dispatch = useDispatch();

  const {
    clueyPlus: {
      getSubscription,
      isLoading: isLoadingClueyPlus,
      hasMultipleActiveSubscriptions,
      toast: { setMessage: setToastMessage, setShow: setToastShow },
      cancellation: { cancellationType },
      specialOffer: { setShow: setShowSpecialOfferMessage },
    },
    sessions: {
      upcoming: { sessions: upcomingSessions, isLoading: isLoadingUpcomingSessions, error: upcomingSessionsError },
    },
    student: { selectedStudent },
  } = useSubscriptionContext();

  const {
    data: accountDetails,
    isLoading: isLoadingAccountDetails,
    error: accountDetailsError,
  } = api.account.details.useQuery();

  const {
    mutate: cancelSubscription,
    isLoading: isLoadingCancelSubscription,
    error: cancelSubscriptionError,
  } = api.clueyPlus.cancelSubscription.useMutation();

  const { subscriptionType, subscriptionId, subscriptionPaidUntilDate, subscriptionEndDate } = getSubscription() ?? {};

  const [isLoadingCancelEnrolment, setIsLoadingCancelEnrolment] = useState(false);
  const [cancelEnrolmentError, setCancelEnrolmentError] = useState<Error | null>(null);

  // Second active subscription if there's  multiple active subscriptions, usually Cluey+ subscription
  const clueyPlusSubscription = useMemo(() => {
    if (!isLoadingClueyPlus && subscriptionType === SUBSCRIPTION_TYPE.TRIAL && hasMultipleActiveSubscriptions) {
      return getSubscription(SUBSCRIPTION_TYPE.CLUEY_PLUS);
    }
  }, [getSubscription, hasMultipleActiveSubscriptions, isLoadingClueyPlus, subscriptionType]);

  // Sorts the date range for the enrolment's upcoming sessions and returns the last paid session date
  const sessionDateRange = useMemo(() => {
    if (enrolment) {
      const forEnrolment = upcomingSessions?.filter((s) => s.uniqueEnrolmentId === enrolment?.enrolmentId) ?? [];
      if (!forEnrolment.length) {
        return { minDate: moment().add(1, 'day'), maxDate: undefined as Date | undefined };
      }
      const sorted = [...forEnrolment].sort((a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf());
      const sortedPaidSessions = sorted.filter((s) => s.paymentStatus === PAYMENT_STATUS.PAID);

      const first = moment(sorted[0]?.startTime);
      const last = moment(sorted[sorted.length - 1]?.startTime);
      const lastPaidSession = moment(sortedPaidSessions[sortedPaidSessions.length - 1]?.startTime);
      return { minDate: first, maxDate: last, lastPaidSession };
    }
    return { minDate: moment().add(1, 'day'), maxDate: undefined as Date | undefined, lastPaidSession: undefined };
  }, [enrolment, upcomingSessions]);

  const defaultCancelDate = sessionDateRange.lastPaidSession
    ? moment(sessionDateRange.lastPaidSession).add(1, 'day')
    : moment().add(1, 'day');

  const datePickerProps = useDatePickerProps({
    labelText: 'Cancel tutoring from',
    placeholder: 'Select cancellation date',
    customInputClasses: 'w-full',
    defaultDate: defaultCancelDate.toDate(),
    options: {
      enableTime: false,
      minDate: defaultCancelDate.toDate(),
      altInput: true,
      altFormat: 'F j, Y',
      dateFormat: 'Y-m-d',
    },
  });

  const onCloseHandler = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const cancelTutorial = useCallback(() => {
    return (
      <>
        {enrolment && (
          <Pending
            loading={isLoadingUpcomingSessions}
            error={upcomingSessionsError ? formatError(upcomingSessionsError) : undefined}
            loader={
              <div className="flex flex-col gap-y-4">
                <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-6 w-32" />
                <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-12 w-full" />
                <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-4 w-full" />
                <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-12 w-full" />
                <SkeletonLoader loading={isLoadingUpcomingSessions} className="mt-4 h-8 w-full" />
                <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-8 w-full" />
              </div>
            }
          >
            <div className="flex flex-col items-center gap-y-6">
              <h2 className="merriweather w-full text-left text-2xl font-bold">Cancel {enrolment.subject} tutoring</h2>
              <div className="flex w-full flex-col gap-y-6 rounded-2xl border border-blood-orange-3 bg-blood-orange-2/40 p-6">
                <p className="text-base font-medium">
                  We&apos;re sorry to see you go. {enrolment.student}'s last paid session is{' '}
                  {moment(sessionDateRange.lastPaidSession).format('DD MMMM')}.
                </p>
                <div>
                  <DatePicker
                    {...datePickerProps}
                    timezone={accountDetails?.timezone}
                    customInputClasses="!rounded-full"
                    icon={<img src={CalendarIcon} alt="calendar icon" width={24} height={24} />}
                  />
                  <p className="pt-1 text-sm text-grey-4">
                    You'll still have full access to your current tutoring sessions until they wrap up.
                  </p>
                </div>
              </div>
              <div className="flex w-full flex-col gap-y-3">
                {cancelEnrolmentError && <p className="text-red-4">{formatError(cancelEnrolmentError)}</p>}
                <Button
                  size="sm"
                  type="submit"
                  disabled={isLoadingCancelEnrolment}
                  className="flex !h-9 w-full flex-row items-center justify-center !border-blood-orange-4 !bg-blood-orange-4 !font-medium text-white"
                  onClick={() => {
                    setIsLoadingCancelEnrolment(true);
                    dispatch(
                      cancelEnrolment({
                        enrolmentId: enrolment.enrolmentSfid,
                        endDate: isArray(datePickerProps.value) ? datePickerProps.value[0] : datePickerProps.value,
                        rating: 5,
                        selectedReasons: [
                          {
                            label: 'Other',
                            apiname: '1_2_3_4_5_Other',
                          },
                        ],
                        message: 'Cancelled thru Subscription page',
                        otherMessage: 'Cancelled thru Subscription page',
                        onSubmitFailed: () => {
                          SubscriptionLocalStorage.remove(
                            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_ENROLMENT_REQUESTED,
                            enrolment?.enrolmentId
                          );
                          setIsLoadingCancelEnrolment(false);
                          setCancelEnrolmentError(
                            new Error(
                              'There was a problem cancelling your enrolment. Please try again later or contact us.'
                            )
                          );
                        },
                        onSubmitSuccess: () => {
                          onClose();
                          SubscriptionLocalStorage.set(
                            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_ENROLMENT_REQUESTED,
                            enrolment?.enrolmentId
                          );
                          setIsLoadingCancelEnrolment(false);
                          if (subscriptionType === SUBSCRIPTION_TYPE.BUNDLED) {
                            setShowSpecialOfferMessage(true);
                            setToastShow(true);
                            setToastMessage(
                              <div className="flex flex-row gap-x-2">
                                <img src={CheckTickIcon} alt="" className="h-6 w-6" />
                                <span className="merriweather text-lg font-bold">
                                  You have cancelled {enrolment.student}&apos;s {enrolment.subject} enrolment
                                </span>
                              </div>
                            );
                          }
                        },
                      })
                    );
                  }}
                >
                  {isLoadingCancelEnrolment ? <InlineLoader /> : 'Cancel tutoring'}
                </Button>
                <Button
                  size="sm"
                  appearance="reverse"
                  className="w-full border-none !font-medium text-grey-6 hover:!bg-white hover:!text-grey-6"
                  onClick={onClose}
                >
                  Keep tutoring
                </Button>
              </div>
            </div>
          </Pending>
        )}
      </>
    );
  }, [
    accountDetails?.timezone,
    cancelEnrolmentError,
    datePickerProps,
    dispatch,
    enrolment,
    isLoadingCancelEnrolment,
    isLoadingUpcomingSessions,
    onClose,
    sessionDateRange.lastPaidSession,
    setShowSpecialOfferMessage,
    setToastMessage,
    setToastShow,
    subscriptionType,
    upcomingSessionsError,
  ]);

  const cancelClueyPlus = useCallback(() => {
    const isOnTrialAndHasMultipleActiveSubscriptions =
      subscriptionType === SUBSCRIPTION_TYPE.TRIAL && hasMultipleActiveSubscriptions;
    const clueyPlusSubscriptionNotStarted =
      clueyPlusSubscription &&
      clueyPlusSubscription.subscriptionStartDate === clueyPlusSubscription.subscriptionPaidUntilDate;

    let currentSubscriptionEndsOn = subscriptionEndDate || subscriptionPaidUntilDate;
    if (isOnTrialAndHasMultipleActiveSubscriptions && clueyPlusSubscriptionNotStarted) {
      currentSubscriptionEndsOn = subscriptionEndDate || subscriptionPaidUntilDate;
    } else if (clueyPlusSubscription) {
      currentSubscriptionEndsOn =
        clueyPlusSubscription?.subscriptionEndDate || clueyPlusSubscription?.subscriptionPaidUntilDate;
    }

    return (
      <>
        <Pending
          loading={isLoadingUpcomingSessions || isLoadingAccountDetails}
          error={upcomingSessionsError ? formatError(upcomingSessionsError) : undefined}
          loader={
            <div className="flex flex-col gap-y-4">
              <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-6 w-32" />
              <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-12 w-full" />
              <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-4 w-full" />
              <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-12 w-full" />
              <SkeletonLoader loading={isLoadingUpcomingSessions} className="mt-4 h-8 w-full" />
              <SkeletonLoader loading={isLoadingUpcomingSessions} className="h-8 w-full" />
            </div>
          }
        >
          <div className="flex flex-col items-center gap-y-6">
            <h2 className="merriweather text-2xl font-bold">End your Cluey+ subscription</h2>
            <div className="flex flex-col gap-y-6">
              <p className="text-lg font-semibold">We're sorry to see you go, {selectedStudent.firstName}.</p>
              <p className="text-base">
                If you end your subscription, you will lose access to the Student Hub and all your recorded progress at
                the end of this billing period.
              </p>
              <div className="flex flex-col gap-y-3 rounded-2xl border border-blood-orange-3 bg-blood-orange-2/40 p-6">
                <p className="text-sm font-medium">Your Cluey+ subscription will end on</p>
                <p className="font-lg font-semibold text-blood-orange-5">
                  {moment(currentSubscriptionEndsOn).format('DD MMM YYYY')}
                </p>
              </div>
            </div>
            <div className="flex w-full flex-col gap-y-3">
              {cancelSubscriptionError && (
                <p className="text-red-4">{formatError(cancelSubscriptionError || accountDetailsError)}</p>
              )}
              <Button
                size="sm"
                type="submit"
                disabled={isLoadingCancelSubscription}
                className="flex !h-9 w-full max-w-sm flex-row items-center justify-center !border-blood-orange-4 !bg-blood-orange-4 !font-medium text-white"
                onClick={() => {
                  cancelSubscription(
                    {
                      accountId: accountDetails?.customerSfid,
                      customerSubscriptionId: subscriptionId,
                      enrolmentCancellationDate: moment(subscriptionPaidUntilDate).format('YYYY-MM-DD'),
                      enrolmentId: enrolment?.enrolmentId,
                    },
                    {
                      onSuccess: () => {
                        SubscriptionLocalStorage.set(
                          SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_SUBSCRIPTION_REQUESTED,
                          clueyPlusSubscription?.subscriptionId || subscriptionId
                        );
                        setToastShow(true);
                        setToastMessage(
                          <div className="flex flex-row gap-x-2">
                            <img src={CheckTickIcon} alt="" className="h-6 w-6" />
                            <span className="merriweather text-lg font-bold">
                              You have ended your Cluey+ subscription
                            </span>
                          </div>
                        );
                        onClose();
                      },
                      onError: (error) => {
                        SubscriptionLocalStorage.remove(
                          SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_SUBSCRIPTION_REQUESTED,
                          clueyPlusSubscription?.subscriptionId || subscriptionId
                        );
                        setToastShow(true);
                        setToastMessage(formatError(error));
                      },
                    }
                  );
                }}
              >
                {isLoadingCancelSubscription ? <InlineLoader /> : 'End subscription'}
              </Button>
              <Button
                size="sm"
                appearance="reverse"
                className="w-full border-none !font-medium text-grey-6 hover:!bg-white hover:!text-grey-6"
                onClick={onClose}
              >
                Keep my subscription
              </Button>
            </div>
          </div>
        </Pending>
      </>
    );
  }, [
    subscriptionType,
    hasMultipleActiveSubscriptions,
    clueyPlusSubscription,
    subscriptionEndDate,
    subscriptionPaidUntilDate,
    isLoadingUpcomingSessions,
    isLoadingAccountDetails,
    upcomingSessionsError,
    selectedStudent.firstName,
    accountDetails?.customerSfid,
    cancelSubscriptionError,
    accountDetailsError,
    isLoadingCancelSubscription,
    onClose,
    cancelSubscription,
    subscriptionId,
    enrolment?.enrolmentId,
    setToastShow,
    setToastMessage,
  ]);

  const cancellationMessage = useCallback(() => {
    switch (cancellationType) {
      case CANCELLATION_TYPE.CANCEL_TUTORIAL:
        return cancelTutorial();
      case CANCELLATION_TYPE.CANCEL_CLUEY_PLUS:
        return cancelClueyPlus();
      default:
        return null;
    }
  }, [cancelClueyPlus, cancelTutorial, cancellationType]);

  return (
    <div
      className="z-9 inset-0 flex flex-col rounded-3xl border border-grey-2/20 bg-white p-7 shadow-md"
      data-testid="cancel-tutoring-modal"
    >
      <div className="flex flex-row justify-end">
        <button type="button" onClick={onCloseHandler} className="p-1 hover:bg-white/10" aria-label="Close">
          <img src={Cross} alt="close icon" />
        </button>
      </div>
      {cancellationMessage()}
    </div>
  );
};
